import React from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { AuthProvider, useAuth } from './components/Auth/AuthContext';
import CookieConsent from './components/Auth/CookieConsent';
import Login from './components/Auth/Login';
import Dashboard from './components/Dashboard/Dashboard';
import MockLayout from './components/MockPrototype/MockLayout';
import LoanAppDemo from './components/LoanAppDemo/LoanAppDemo';
import LiveStampingVendorPortal from './pages/LiveStampingVendorPortal';
import LiveStampingAdminConsole from './pages/LiveStampingAdminConsole';
import ForgotPassword from './components/Auth/ForgotPassword';
import ResetPassword from './components/Auth/ResetPassword';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import './utils/axiosConfig'; // Enable cookie-based auth for all axios calls
import './App.css';

const MainApp = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-on-light">Loading...</p>
        </div>
      </div>
    );
  }

  return user ? <Dashboard /> : <Login />;
};

/**
 * ProtectedLoanDemo — Wrapper that ensures the user is logged in
 * before rendering the demo. This safeguards the API key usage.
 */
const ProtectedLoanDemo = () => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
          <p className="mt-4 text-on-light">Verifying session...</p>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Login />;
  }

  return <LoanAppDemo />;
};

const App = () => {
  return (
    <CookieConsent>
      <BrowserRouter>
        <Routes>
          {/* Isolated Mock Prototype Route */}
          <Route path="/mock-demo/*" element={<MockLayout />} />

          {/* Main Application Routes (includes protected demo) */}
          <Route path="/*" element={
            <AuthProvider>
              <Routes>
                {/* Loan Demo — protected by session verification */}
                <Route path="/loan-demo" element={<ProtectedLoanDemo />} />

                {/* Live Stamping Vendor Portal — vendor + admin only */}
                <Route path="/live-stamping/vendor" element={<LiveStampingVendorPortal />} />

                {/* Live Stamping Admin Console — admin only */}
                <Route path="/live-stamping/admin" element={<LiveStampingAdminConsole />} />

                {/* Password Reset Flow — public, accessible without authentication */}
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/reset-password" element={<ResetPassword />} />

                {/* All other routes */}
                <Route path="/*" element={<MainApp />} />
              </Routes>
              <ToastContainer
                position="top-right"
                autoClose={3000}
                hideProgressBar={false}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="light"
              />
            </AuthProvider>
          } />
        </Routes>
      </BrowserRouter>
    </CookieConsent>
  );
};

export default App;